import process from 'node:process';
import React from 'react';
import type { LoginMode } from '@wix/cli-core-definitions';
import { LoginModeProvider } from '@wix/cli-auth';
import type { UserInfo } from '@wix/cli-auth';
import { BiProvider } from '@wix/cli-telemetry-react';
import { render } from '@wix/cli-ui-kit';
import { Command, Option } from '@commander-js/extra-typings';
import { ErrorReporterProvider } from '@wix/cli-error-reporting';
import type { ErrorReporter } from '@wix/cli-error-reporting';
import type { Panorama } from '@wix/cli-panorama';
import { createCommandTransaction, PanoramaProvider } from '@wix/cli-panorama';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { validateBusinessName } from './validations/validate-business-name.js';
import { validateFolderName } from './validations/folder-name.js';
import { LinkCommand } from './components/LinkCommand/LinkCommand.js';
import { validateSiteTemplate } from './validations/site-template.js';
import {
  DEFAULT_HEADLESS_TEMPLATE_NAME,
  pureHeadlessTemplates,
} from './parse-command-options.js';
import { validateGitUrl } from './validations/git-url.js';
import { validateTemplateParams } from './validations/validate-template-params.js';
import { ProcessCommand } from './components/ProcessCommand.js';
import { createBiLogger } from './bi/index.js';
import { validateDirectoryExists } from './validations/directory-exists.js';
export { VibeRepositorySetupPolling } from './components/VibeRepositorySetupPolling.js';
export { getHeadlessInitCommand } from './get-headless-init-command.js';

function assertInteractiveTerminalIfNeeded(
  hasRequiredFlagsForNonInteractive: boolean
) {
  if (!process.stdin.isTTY && !hasRequiredFlagsForNonInteractive) {
    throw new CliError({
      code: CliErrorCode.NonInteractiveTerminal(),
      cause: null,
    });
  }
}

export const getHeadlessCommand = ({
  errorReporter,
  userInfo,
  loginMode,
  panorama,
  cliVersion,
  cliSessionId,
  targetParentFolder,
  aiAgentName,
}: {
  errorReporter: ErrorReporter;
  userInfo: UserInfo | null;
  loginMode?: LoginMode;
  panorama: Panorama;
  cliVersion: string;
  cliSessionId: string;
  targetParentFolder: string;
  aiAgentName?: string;
}) => {
  const program = new Command()
    .name('headless')
    .description('Create a Wix headless site');

  program
    .command('link')
    .description('Link current project to use Wix as deployment target')
    .addOption(
      new Option(
        '--business-name <business-name>',
        'The name of your business'
      ).argParser(validateBusinessName)
    )
    .action(async (options, command) => {
      errorReporter.setContext('Command Info', {
        Arguments: command.args,
        Options: command.opts(),
      });

      assertInteractiveTerminalIfNeeded(Boolean(options.businessName));

      const { biLogger, reportCommandStartEvent } = await createBiLogger({
        errorReporter,
        cliVersion,
        cliSessionId,
        userId: userInfo?.userId,
        aiAgentName,
        command: 'link-headless-site',
      });

      const commandStatus = reportCommandStartEvent(command);
      const transaction = createCommandTransaction(panorama, command);

      try {
        transaction.start();

        await render(
          <BiProvider value={biLogger}>
            <ErrorReporterProvider value={errorReporter}>
              <PanoramaProvider value={panorama}>
                <LoginModeProvider value={loginMode}>
                  <LinkCommand
                    businessName={options.businessName}
                    projectFolder={targetParentFolder}
                  />
                </LoginModeProvider>
              </PanoramaProvider>
            </ErrorReporterProvider>
          </BiProvider>
        );

        transaction.finish();
        commandStatus.succeed();
      } catch (e) {
        commandStatus.failed(e);
        throw e;
      }
    });

  program
    .command('init', { isDefault: true })
    .description('Create headless site')
    .addOption(
      new Option('--skip-install', 'Skip automatic dependency installation')
    )
    .addOption(
      new Option(
        '--skip-git',
        'Skip initializing a Git repository during setup'
      )
    )
    .addOption(
      new Option(
        '--site-template [site-template]',
        'Site template to use for the app creation'
      )
        .choices(pureHeadlessTemplates.map((template) => template.name))
        .argParser(validateSiteTemplate)
        .preset(DEFAULT_HEADLESS_TEMPLATE_NAME)
    )
    .addOption(
      new Option(
        '--template-repo <template-repo>',
        'Git repository URL to fetch the template from'
      )
        .argParser(validateGitUrl)
        .hideHelp()
    )
    .addOption(
      new Option(
        '--template-repo-path <template-repo-path>',
        'Path inside the Git repository to fetch the template from'
      ).hideHelp()
    )
    .addOption(
      new Option(
        '--template-path <template-path>',
        'Local path to the template folder'
      )
        .argParser(validateDirectoryExists)
        .hideHelp()
    )
    .addOption(
      new Option(
        '--template-params <template-params>',
        'Additional template parameters to be provided to the app template should be formatted as a JSON string. These parameters can be accessed through the templateParams variable.'
      )
        .argParser(validateTemplateParams)
        .hideHelp()
    )
    .addOption(
      new Option(
        '--business-name <business-name>',
        'The name of your business'
      ).argParser(validateBusinessName)
    )
    .addOption(
      new Option(
        '--folder-name <folder-name>',
        'The name of your project folder'
      ).argParser(validateFolderName)
    )
    .addOption(
      new Option(
        '--no-publish',
        'Do not publish your site at the end of the creation.'
      )
    )
    .addOption(
      new Option('--publish', 'Publish your site at the end of the creation.')
        .default(true)
        .hideHelp()
    )
    .version(cliVersion, '-v, --version')
    .action(async (options, command) => {
      errorReporter.setContext('Command Info', {
        Arguments: command.args,
        Options: command.opts(),
      });

      assertInteractiveTerminalIfNeeded(
        Boolean(
          options.siteTemplate && options.businessName && options.folderName
        )
      );

      const { biLogger, reportCommandStartEvent } = await createBiLogger({
        errorReporter,
        cliVersion,
        cliSessionId,
        userId: userInfo?.userId,
        aiAgentName,
        command: 'create-headless-site',
      });

      const commandStatus = reportCommandStartEvent(command);
      const transaction = createCommandTransaction(panorama, command);

      try {
        transaction.start();

        await render(
          <BiProvider value={biLogger}>
            <ErrorReporterProvider value={errorReporter}>
              <PanoramaProvider value={panorama}>
                <LoginModeProvider value={loginMode}>
                  <ProcessCommand
                    targetParentFolder={targetParentFolder}
                    commandOptions={options}
                  />
                </LoginModeProvider>
              </PanoramaProvider>
            </ErrorReporterProvider>
          </BiProvider>
        );

        transaction.finish();
        commandStatus.succeed();
      } catch (e) {
        commandStatus.failed(e);
        throw e;
      }
    });

  return program;
};
